/** La pelle dei moduli del sito, scritta una volta sola.
 *
 *  I moduli sono due, /contatti e il briefing di /accoglienza, e fino al
 *  30/09/2026 parlavano due lingue: su /contatti ogni passaggio aveva un
 *  tono suo (viola, teal, terracotta, e il bottone d'invio verde), le
 *  etichette erano in maiuscoletto spaziato a dieci pixel e il bordo del
 *  fuoco lo cambiava JavaScript in `onFocus`/`onBlur`; sul briefing le
 *  etichette erano in tondo, i bordi neutri e il bottone era il pieno
 *  sabbia del resto del sito. Chi passava dall'uno all'altro trovava due
 *  moduli di due siti.
 *
 *  Il riferimento è il briefing, che era già allineato: etichetta in
 *  tondo a `text-corpo` (sette etichette vicine in maiuscolo sotto i
 *  quattordici pixel non si leggono), bordo neutro, accento solo dove
 *  qualcosa è scelto o ha il fuoco, e il rosso della scala solo dove
 *  qualcosa non va.
 *
 *  Tutti gli stati passano dal CSS e non da uno `style` inline: un inline
 *  vincerebbe su qualunque classe di stato, ed è per questo che prima il
 *  fuoco andava riscritto a mano in due gestori per campo.
 *
 *  Modulo senza direttiva: lo leggono sia i due moduli client sia le
 *  pagine server (la stessa ragione di `components/ui/pulsanti.ts`). */

import { FUOCO } from "@/components/ui/pulsanti";

/** Il bordo a riposo è `sommesso` e non `rule`: il filetto delle
 *  separazioni sulla carta avorio sta a 1,5:1, e il contorno di un campo
 *  da compilare (WCAG 1.4.11) ne vuole almeno 3. `sommesso` è 5,3:1 nel
 *  chiaro e 6,1:1 nello scuro. */
const BORDO_CAMPO =
  "border border-sommesso hover:border-ink focus:border-accent aria-[invalid=true]:border-[var(--p-teatri-crisi)]";

/** Il fuoco da tastiera: lo stesso contorno di tutti i pulsanti del sito
 *  (`FUOCO`), rosso se il campo porta un errore. Un campo di testo prende
 *  `:focus-visible` anche col clic, quindi il contorno si vede sempre. */
const FUOCO_CAMPO = `${FUOCO} aria-[invalid=true]:focus-visible:outline-[var(--p-teatri-crisi)]`;

/** Input e textarea. */
export const CAMPO = `w-full rounded-[2px] bg-bg px-3 py-2.5 font-sans text-corpo text-ink placeholder:text-muted transition-colors ${BORDO_CAMPO} ${FUOCO_CAMPO}`;

/** L'etichetta di un campo: tondo, `text-corpo`, semigrassetto. */
export const ETICHETTA = "font-sans text-corpo font-semibold leading-tight text-ink";

/** La nota accanto all'etichetta: «(facoltativo)», «(uno o più)». */
export const ETICHETTA_NOTA = "font-normal text-muted";

/** Una scelta fra più voci (tipo di organizzazione, servizi, obiettivi).
 *  Vale sia per un `<button aria-pressed>` sia per un `<label>` che
 *  avvolge una casella: `aria-pressed:` accende il primo, `has-checked:`
 *  il secondo, e `has-focus-visible:` dà al `<label>` il contorno del
 *  fuoco della casella che contiene. */
export const SCELTA = `inline-flex cursor-pointer items-center gap-2 rounded-[2px] border border-sommesso bg-transparent px-3 py-2 font-sans text-meta text-ink transition-colors hover:border-ink disabled:cursor-not-allowed disabled:opacity-50 aria-pressed:border-accent aria-pressed:bg-accentSoft has-checked:border-accent has-checked:bg-accentSoft ${FUOCO} has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-accent`;

/** Il riquadro di un gruppo di campi (un passaggio, l'informativa). */
export const GRUPPO =
  "relative min-w-0 rounded-sm border border-rule bg-surface p-5 transition-opacity disabled:opacity-60 md:p-6";

/** La legenda di un gruppo: un dato di collocazione, quindi monospazio
 *  maiuscolo come gli occhielli. Il colore lo aggiunge chi la usa:
 *  `text-accent` sui passaggi, `text-muted` sull'informativa. */
export const LEGENDA =
  "ml-2 px-2 font-mono text-nota font-bold uppercase tracking-etichetta";
